import { useDebounce } from "@/hooks/debounce";
import { usePermissions } from "@/hooks/usePermissions";
import { getManagers } from '@/store/slices/userSlice';
import { getCountryList } from "@/store/slices/customerSlice";
import { useAppDispatch } from "@/store/hooks";
import type { RootState } from "@/store/rootReducer";
import { getLocationCoverageReportDetails, getLocationDistanceCoverageExport } from "@/store/slices/locationCoverageReportSlice";
import { getMyTeamGCDetail } from "@/store/slices/teamSlice";
import dayjs from "dayjs";
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import type { RDTColumn } from "@/components/Table/Table";
import { Box, Button, CircularProgress, TablePagination, TextField, Tooltip, Typography } from "@mui/material";
import { ButtonLoader } from "@/components/Loader/ButtonLoader";
import Table from "@/components/Table/Table";
import FilterComponent from '@/pages/FilterComponent/FilterComponent';
import AppliedFilter from '@/pages/FilterComponent/AppliedFilter';
import Select from "@/components/FormControl/Select/Select";
import { months } from "@/lib/methodUtils";
import type { showFilters } from "@/pages/FilterComponent/FilterInterface";
import AttendanceBadge from "@/components/AttendanceBadge/AttendanceBadge";

const LocationCoverageReport: React.FC = () => {
    const dispatch = useAppDispatch();
    const { locationCoverageReportDetails, loading } = useSelector((state: RootState) => state.locationCoverageReport);
    const { currentUser, permissionLoading } = usePermissions();

    const { managerList } = useSelector((state: RootState) => state.user);
    const { teams } = useSelector((state: RootState) => state.team);
    const { countryList } = useSelector((state: RootState) => state.customer);

    const currentMonth = dayjs().month() + 1;
    const currentYear = dayjs().year();

    const [page, setPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [sortState, setSortState] = useState<Record<string, "asc" | "desc">>({});
    const [sortBy, setSortBy] = useState("");

    const [search, setSearch] = useState("");
    const debouncedSearch = useDebounce((search || "").trim());

    const [isApplying, setIsApplying] = useState(false);
    const applyTriggeredRef = useRef(false);

    const [filterAnchor, setFilterAnchor] = useState<HTMLElement | null>(null);

    const [filterData, setFilterData] = useState<any>({
        managerIds: [],
        userIds: [],

        location: {
            country: null,
            state: null,
            city: null,
            locality: null,
        },
    });

    const [filters, setFilters] = useState({
        month: currentMonth,
        year: currentYear,
    });

    const [appliedFilters, setAppliedFilters] = useState({
        month: currentMonth,
        year: currentYear,
    });

    const filteredMonths = useMemo(() => {
        if (filters.year === currentYear) {
            return months.filter(
                (m) => m.value <= currentMonth
            );
        }

        return months;
    }, [filters.year, currentYear, currentMonth]);

    const years = Array.from({ length: 5 }, (_, i) => {
        const y = currentYear - 2 + i;
        return {
            value: y,
            label: y
        };

    }).filter((y) => y.value <= currentYear);

    const list = {
        managerList: managerList || [],
        userList: teams?.Members || [],
        countryList: countryList || [],
    };

    const showFilters: showFilters = {
        manager: { show: true, mode: "multi" },
        user: { show: true, mode: "multi" },
        location: {
            country: { show: true, mode: "single" },
            state: { show: true, mode: "single" },
            city: { show: false, mode: "single" },
            locality: { show: false, mode: "single" },
        },
    };

    const queryParams = useMemo(() => ({
        From: (page - 1) * rowsPerPage + 1,
        To: page * rowsPerPage,
        Search: debouncedSearch?.trim() ? debouncedSearch : null,
        ManagerIds: (filterData.managerId || []).map((m: any) => m.Id).join(",") || null,
        UserIds: (filterData.userIds || []).map((u: any) => u.UserId).join(",") || null,
        CountryId: filterData?.location?.country?.CountryId || 0,
        StateId: filterData?.location?.state?.StateId || 0,
        Month: appliedFilters.month,
        Year: appliedFilters.year,
        sortBy: sortBy?.trim() ? sortBy : null,
    }), [
        page,
        rowsPerPage,
        currentUser,
        debouncedSearch,
        filterData,
        sortBy,
        appliedFilters,
    ]);

    const fetchLocationReport = useCallback(() => {
        if (!currentUser?.userId || permissionLoading) return;
        dispatch(getLocationCoverageReportDetails(queryParams));
    }, [
        dispatch,
        queryParams,
        currentUser,
        permissionLoading
    ]);

    useEffect(() => {
        fetchLocationReport();
    }, [fetchLocationReport]);

    useEffect(() => {
        if (applyTriggeredRef.current && !loading.locationCoverageReportDetails) {
            setIsApplying(false);
            applyTriggeredRef.current = false;
        }
    }, [loading?.locationCoverageReportDetails]);

    useEffect(() => {
        dispatch(getManagers());
        dispatch(getCountryList());
        dispatch(
            getMyTeamGCDetail({
                from: 1,
                to: -1,
                text: "",
                Status: "all"
            })
        );
    }, [dispatch]);

    useEffect(() => {
        setPage(1);
    }, [debouncedSearch]);

    const applyFilter = useCallback((data: any) => {
        setFilterData(data);
        setPage(1);
        setFilterAnchor(null);
    }, []);

    const clearFilter = useCallback((data: any) => {
        setFilterData(data);
        setPage(1);
    }, []);

    const handleApply = () => {
        if (isApplying) return;

        applyTriggeredRef.current = true;
        setPage(1);
        setAppliedFilters({
            ...filters
        });

        setIsApplying(true);
    };

    const handleSortChange = (sortValue: string) => {
        setSortBy(sortValue);
        setPage(1);
    };

    const handleExport = useCallback(() => {
        const exportParams = {
            ...queryParams,
            To: -1,
            From: 1,
        };

        dispatch(
            getLocationDistanceCoverageExport(
                exportParams
            )
        );

    }, [dispatch, queryParams]);

    const daysInMonth = useMemo(() => {
        const date = dayjs(`${appliedFilters.year}-${appliedFilters.month}-01`);
        const count = date.daysInMonth();

        return Array.from({ length: count }, (_, i) => {
            const d = date.date(i + 1);
            return {
                date: i + 1,
                dayName: d.format("ddd"),
                fullDate: d.format("YYYY-MM-DD"),
            };
        });
    }, [appliedFilters]);

    const leftColumns: RDTColumn[] = [
        {
            name: "Name",
            width: "120px",
            selector: (row: any) => row?.Name || "-"
        },
        {
            name: "Manager",
            width: "120px",
            selector: (row: any) => row?.Manager || "-",
        },
        {
            name: "Emp No.",
            width: "120px",
            selector: (row: any) => row?.EmployeeNo || "-",
        },
        {
            name: "State",
            width: "120px",
            selector: (row: any) => row?.State || "-",
        },
        {
            name: "Total KM",
            width: "120px",
            selector: (row: any) => row?.TotalDistance || 0
        }
    ];

    const dayColumns: RDTColumn[] =
        daysInMonth?.map((day: any) => ({
            name: (
                <div className="flex flex-col items-center leading-tight">
                    <span className="font-semibold">
                        {day.date}
                    </span>
                    <span className="text-[10px] text-gray-500">
                        {day.dayName}
                    </span>
                </div>
            ),

            width: "90px",
            align: "center",
            customRender: (row: any) => {

                const dayData =
                    row.Days?.[String(day.date)];

                const distance = Number(dayData?.Distance ?? 0);

                const formattedDistance =
                    distance > 999
                        ? `${distance.toString().slice(0, 3)}...`
                        : distance;

                const showAttendance = distance <= 0;

                return (
                    <div className="group relative flex items-center justify-center">
                        <div className="text-sm font-semibold text-gray-700 cursor-pointer">
                            {showAttendance ? (
                                <AttendanceBadge status={dayData?.Status} />
                            ) : (
                                formattedDistance
                            )}
                        </div>

                        {!showAttendance && distance > 999 && (
                            <div className="absolute bottom-[85%] z-50 hidden group-hover:flex items-center justify-center whitespace-nowrap rounded-md border border-gray-300 bg-white px-2 py-1 text-sm font-medium text-black shadow-md">
                                {distance}
                            </div>
                        )}
                    </div>
                );
            }
        }));

    if (permissionLoading) {
        return null;
    }

    const tableData = locationCoverageReportDetails?.Items || [];

    return (
        <div className="p-4">
            <div className="pb-6 mb-7.5">
                <div className="flex justify-between items-center border-b border-gray-200 pb-4 mb-4">
                    {/* left side */}
                    <Button
                        variant="contained"
                        onClick={(e) => setFilterAnchor(e.currentTarget)}
                    >
                        Filter
                    </Button>

                    <FilterComponent
                        anchorEl={filterAnchor}
                        onClose={() => setFilterAnchor(null)}
                        onApply={applyFilter}
                        appliedFilter={filterData}
                        list={list}
                        showFilters={showFilters}
                        filterName="locationCoverage"
                        width="500px"
                    />

                    {/* right side */}
                    <div className="flex items-end gap-4 ml-auto">
                        {/* Month */}
                        <div className="w-[80px]">
                            <Select
                                field={{
                                    value: filters.month,
                                    onChange: (val) =>
                                        setFilters((p) => ({
                                            ...p,
                                            month: Number(val),
                                        }))
                                }}
                                label="By Month"
                                options={filteredMonths}
                                idKey="value"
                                nameKey="label"
                            />
                        </div>

                        {/* Year */}
                        <div className="w-[80px]">
                            <Select
                                field={{
                                    value: filters.year,
                                    onChange: (val) => {
                                        const selectedYear = Number(val);

                                        setFilters((p) => ({
                                            ...p,
                                            year: selectedYear,
                                            month:
                                                selectedYear === currentYear &&
                                                    p.month > currentMonth
                                                    ? currentMonth
                                                    : p.month,
                                        }));
                                    }
                                }}
                                label="By Year"
                                options={years}
                                idKey="value"
                                nameKey="label"
                            />
                        </div>

                        {/* Search */}
                        <div className="w-[120px]">
                            <TextField
                                label="Search"
                                variant="standard"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                fullWidth
                            />
                        </div>

                        <ButtonLoader
                            variant="contained"
                            onClick={handleApply}
                            loading={isApplying}
                        >
                            Apply
                        </ButtonLoader>

                        <ButtonLoader
                            variant="contained"
                            onClick={handleExport}
                            loading={loading?.locationDistanceCoverageExport}
                        >
                            Export
                        </ButtonLoader>
                    </div>
                </div>

                <AppliedFilter
                    filterData={filterData}
                    onClearFilter={clearFilter}
                    showFilters={showFilters}
                />

                {loading?.locationCoverageReportDetails ? (
                    <div className="flex justify-center items-center h-75">
                        <CircularProgress />
                    </div>
                ) : tableData.length === 0 ? (
                    <div className="flex justify-center items-center h-75">
                        <Typography color="textSecondary">
                            No Records Found
                        </Typography>
                    </div>
                ) : (

                    <div>
                        <div className="flex">
                            <div className="w-[650px] border-r border-gray-200">
                                <Table
                                    columns={leftColumns}
                                    data={tableData}
                                    isGrid
                                    tableWidth="max-content"
                                    sortState={sortState}
                                    setSortState={setSortState}
                                    onSortChange={handleSortChange}
                                />
                            </div>

                            <div className="flex-1 overflow-x-auto">
                                <Table
                                    columns={dayColumns}
                                    data={tableData}
                                    isGrid
                                    isTileHeader
                                    tableWidth="max-content"
                                    onSortChange={handleSortChange}
                                />
                            </div>

                        </div>

                        <TablePagination
                            rowsPerPageOptions={[
                                10,
                                30,
                                50,
                                100
                            ]}
                            component="div"
                            count={
                                locationCoverageReportDetails?.TotalRecords || 0
                            }
                            rowsPerPage={rowsPerPage}
                            page={page - 1}
                            onPageChange={(_, newPage) =>
                                setPage(newPage + 1)
                            }
                            onRowsPerPageChange={(e) => {

                                setRowsPerPage(
                                    parseInt(
                                        e.target.value
                                    )
                                );
                                setPage(1);
                            }}
                        />
                    </div>
                )}
            </div>
        </div>
    );
}

export default memo(LocationCoverageReport);